import useAuthStore from "@/store/authStore";
import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Outlet, useLocation } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import Spinner from "../ui/Spinner";
import useGlobalStore from "@/store/globalStore";

const PrivateRoute: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Where to send the user after they sign in.
   *
   * Without this, any deep link into the app is lost at the login redirect — the user
   * lands on the dashboard and has to find their way back. That matters most for links
   * arriving from email (retention downloads, invites), where the recipient often has no
   * live session at the moment they click.
   *
   * Only the path and query are carried, never a full URL, so the value cannot be turned
   * into an open redirect to another host.
   */
  const loginPath = `/login?redirect=${encodeURIComponent(
    `${location.pathname}${location.search}`,
  )}`;
  const { user, getAuth } = useAuthStore(
    useShallow((state) => ({
      user: state.user,
      getAuth: state.getAuth,
    })),
  );
  const setError = useGlobalStore((state) => state.setError);

  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      await getAuth();
    } catch (error) {
      setError(error.message);
      navigate(loginPath);
    }

    setLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, [getAuth]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner size={40} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={loginPath} replace />;
  }

  return <Outlet />;
};

export default PrivateRoute;
